/**
 * Token-based session, NEVER cookies (§9.2 hard constraint: third-party
 * cookies die inside iframes). Memory-first with best-effort persistence
 * to an injected StorageLike (sessionStorage in the browser); a broken or
 * absent storage (Safari private mode) degrades to memory-only.
 *
 * B20b(h): guest tokens are short-lived, so the session carries the
 * token's deadline and `token()` refuses to hand back an expired one —
 * the guest gate re-mints instead of the API bouncing a dead bearer.
 */

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface Session {
  token(): string | undefined;
  /** No deadline means the token is trusted until cleared (staff dev tokens). */
  setToken(token: string, expiresAt?: number): void;
  clearToken(): void;
  cartId(): string | undefined;
  setCartId(cartId: string): void;
  clearCartId(): void;
}

const TOKEN_KEY = "transact.storefront.token";
const TOKEN_EXP_KEY = "transact.storefront.tokenExpiresAt";
const CART_KEY = "transact.storefront.cartId";

export function createSession(storage: StorageLike | null, now: () => number = Date.now): Session {
  const read = (key: string): string | undefined => {
    try {
      return storage?.getItem(key) ?? undefined;
    } catch {
      return undefined;
    }
  };
  const write = (key: string, value: string): void => {
    try {
      storage?.setItem(key, value);
    } catch {
      /* memory carries it */
    }
  };
  const remove = (key: string): void => {
    try {
      storage?.removeItem(key);
    } catch {
      /* memory carries it */
    }
  };

  // Memory is authoritative; storage hydrates once and persists best-effort.
  let token = read(TOKEN_KEY);
  let cartId = read(CART_KEY);
  let expiresAt: number | undefined;
  const hydratedExp = read(TOKEN_EXP_KEY);
  if (hydratedExp !== undefined) {
    const parsed = Number(hydratedExp);
    if (Number.isFinite(parsed)) expiresAt = parsed;
    else token = undefined; // a garbled deadline poisons the token: fail closed
  }

  const clearToken = (): void => {
    token = undefined;
    expiresAt = undefined;
    remove(TOKEN_KEY);
    remove(TOKEN_EXP_KEY);
  };

  return {
    token: () => {
      if (token !== undefined && expiresAt !== undefined && now() >= expiresAt) clearToken();
      return token;
    },
    setToken: (value, deadline) => {
      token = value;
      expiresAt = deadline;
      write(TOKEN_KEY, value);
      if (deadline !== undefined) write(TOKEN_EXP_KEY, String(deadline));
      else remove(TOKEN_EXP_KEY);
    },
    clearToken,
    cartId: () => cartId,
    setCartId: (value) => {
      cartId = value;
      write(CART_KEY, value);
    },
    clearCartId: () => {
      cartId = undefined;
      remove(CART_KEY);
    },
  };
}
